import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Signature, Lock, Eye, Code as CodeIcon, ArrowSquareOut, Trash } from '@phosphor-icons/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useSignatureStore } from '@/stores/signatureStore'
import { usePlanFeatures } from '@/hooks/usePlanFeatures'
import { usePermission } from '@/hooks/usePermission'
import { SectionLocked } from './SectionAccess'
import { newlinesToBr } from '@/lib/signatureHtml'
import { cardChromeClass, iconChipClass, iconChipAmberClass } from '@/lib/styleKit'

const inputClass =
  'w-full px-3 py-2 rounded-md border border-border/60 bg-background text-[13px] text-foreground ' +
  'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[#5e6ad2] ' +
  'dark:bg-white/[0.03] dark:border-white/[0.08] dark:text-[#f7f8f8]'

const PLACEHOLDER = ['e.g.', 'Jane Doe', 'Head of Growth, Acme Inc.', 'jane@acme.com  |  acme.com'].join('\n')

const SignaturePreview: React.FC<{ html: string }> = ({ html }) => (
  <div className="rounded-lg border border-dashed border-border/60 dark:border-white/[0.08] bg-background dark:bg-white/[0.02] p-3 min-h-[120px]">
    {html.trim() ? (
      <div
        className="text-sm text-foreground dark:text-[#e2e4e7]"
        style={{ fontFamily: 'Arial, Helvetica, sans-serif', lineHeight: 1.5 }}
        dangerouslySetInnerHTML={{ __html: newlinesToBr(html) }}
      />
    ) : (
      <p className="text-xs text-muted-foreground/70 italic">No signature yet.</p>
    )}
  </div>
)

/**
 * Settings > Signatures: YOUR signature, Momentum/Enterprise/Trial only.
 * Nobody else's signature is ever shown here -- there is no list, no name
 * field, no "created by". Each mailbox also keeps its own signature under
 * Mailboxes on every plan (see EmailAccountsSection).
 */
const SignaturesSection: React.FC = () => {
  const navigate = useNavigate()
  const { mine, loaded, planLocked, fetchMine, saveMine, deleteMine } = useSignatureStore()
  const { isLocked } = usePlanFeatures()
  const lockedByPlan = isLocked('per_user_signatures')
  const locked = lockedByPlan || planLocked
  const { allowed } = usePermission('signatures', 'manage')
  // Owner/Admin only, the same cell the Billing nav item is gated on.
  const { canWrite: canUpgradePlan } = usePermission('billing', 'manage_subscription')

  const [html, setHtml] = useState('')
  const [mode, setMode] = useState<'edit' | 'preview'>('edit')
  const [isSaving, setIsSaving] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [confirmingRemove, setConfirmingRemove] = useState(false)

  useEffect(() => {
    if (lockedByPlan || !allowed) return
    fetchMine()
  }, [lockedByPlan, allowed, fetchMine])

  useEffect(() => {
    setHtml(mine?.html ?? '')
  }, [mine])

  if (!allowed) {
    return (
      <SectionLocked title="Signatures are managed by your team's senders">
        Your role does not include sending email, so there is no signature to manage here.
      </SectionLocked>
    )
  }

  const handleSave = async () => {
    const trimmed = html.trim()
    if (!trimmed) {
      toast.error('Please enter the signature itself')
      return
    }
    setIsSaving(true)
    try {
      await saveMine(trimmed)
    } catch {
      // store already toasted
    } finally {
      setIsSaving(false)
    }
  }

  const handleRemove = async () => {
    setConfirmingRemove(false)
    setIsRemoving(true)
    try {
      await deleteMine()
    } catch {
      // store already toasted
    } finally {
      setIsRemoving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}>
          Signatures
        </h2>
        <p className="text-muted-foreground dark:text-[#8a8f98] mt-1" style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}>
          Your signature. Used when you launch a campaign and when you send from a mailbox you have access to.
        </p>
      </div>

      {locked ? (
        <Card className={cardChromeClass} data-testid="signature-plan-locked">
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className={iconChipAmberClass}>
                <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400" weight="fill" />
              </div>
              <div>
                <CardTitle>Your own signature</CardTitle>
                <CardDescription>Available on the Momentum and Enterprise plans</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}>
                Upgrade to Momentum or Enterprise for your own signature, used automatically on every send. Mailbox signatures keep working on every plan.
              </p>
              {/* Only Owner/Admin hold billing.manage_subscription, and the
                  Billing nav item is hidden for everyone else. Offering the
                  button anyway sends them to a page that exists only to say
                  billing is not theirs. The sentence above still explains
                  why signatures are locked. */}
              {canUpgradePlan && (
                <Button variant="outline" size="sm" onClick={() => navigate('/billing')}>
                  <ArrowSquareOut className="w-4 h-4 mr-1.5" weight="bold" />
                  Upgrade plan
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className={cardChromeClass}>
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className={iconChipClass}>
                <Signature className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
              </div>
              <CardTitle>Your signature</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {!loaded ? (
              <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px' }}>Loading…</p>
            ) : (
              <>
                <div className="flex items-center justify-end">
                  <div className="inline-flex rounded-md border border-border/60 dark:border-white/[0.08] overflow-hidden">
                    <button type="button" onClick={() => setMode('edit')} className={`px-2.5 py-1 text-[11px] font-medium inline-flex items-center gap-1 transition-colors ${mode === 'edit' ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc]' : 'text-muted-foreground hover:bg-muted/40 dark:hover:bg-white/[0.04]'}`}>
                      <CodeIcon className="w-3 h-3" /> Edit
                    </button>
                    <button type="button" onClick={() => setMode('preview')} className={`px-2.5 py-1 text-[11px] font-medium inline-flex items-center gap-1 transition-colors border-l border-border/60 dark:border-white/[0.08] ${mode === 'preview' ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc]' : 'text-muted-foreground hover:bg-muted/40 dark:hover:bg-white/[0.04]'}`}>
                      <Eye className="w-3 h-3" /> Preview
                    </button>
                  </div>
                </div>
                {mode === 'edit' ? (
                  <textarea
                    aria-label="Signature"
                    value={html}
                    onChange={(e) => setHtml(e.target.value)}
                    placeholder={PLACEHOLDER}
                    rows={6}
                    spellCheck={false}
                    className={`${inputClass} font-mono leading-relaxed resize-none`}
                  />
                ) : (
                  <SignaturePreview html={html} />
                )}
                <div className="flex justify-end gap-2">
                  {mine && (
                    <Button variant="outline" size="sm" loading={isRemoving} onClick={() => setConfirmingRemove(true)}>
                      <Trash className="w-4 h-4 mr-1.5" weight="bold" />
                      Remove signature
                    </Button>
                  )}
                  <Button variant="default" size="sm" loading={isSaving} onClick={handleSave}>
                    Save signature
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={confirmingRemove}
        title="Remove your signature"
        description="This removes your personal signature. You can add a new one at any time."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        tone="destructive"
        onConfirm={handleRemove}
        onCancel={() => setConfirmingRemove(false)}
      />
    </div>
  )
}

export default SignaturesSection
